import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Lock,
  Plus,
  Wallet,
  TrendingUp,
  TrendingDown,
  Download,
  X,
} from 'lucide-react';
import { caisseApi } from '../../services/caisseApi';
import type { CategorieOperation, TypeOperation } from '../../types/caisse';
import { formatCurrency } from '../../utils/formatters';
import { downloadFile } from '../../utils/download';
import FolioDateNav from './FolioDateNav';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const FolioJournalier: React.FC = () => {
  const queryClient = useQueryClient();
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [showForm, setShowForm] = useState(false);

  const { data: folio, isLoading } = useQuery(
    ['caisse', 'folio', date],
    () => caisseApi.getFolioDuJour(date),
  );

  const refresh = () => queryClient.invalidateQueries(['caisse', 'folio', date]);

  const cloturer = useMutation(
    () => caisseApi.cloturerFolio(folio!.id as number),
    { onSuccess: refresh },
  );

  if (isLoading || !folio) {
    return <div className="p-6 text-gray-500">Chargement du folio…</div>;
  }

  const operations = folio.operations || [];

  return (
    <div className="w-full space-y-6">
      {/* Sélecteur de date + clôture */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Date du folio</label>
          <FolioDateNav value={date} onChange={setDate} />
          {folio.id === null && (
            <span className="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
              Aucun folio ouvert ce jour-là
            </span>
          )}
          {folio.cloture && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
              <Lock className="h-3.5 w-3.5" /> Folio clôturé
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {folio.id !== null && (
            <button
              onClick={() => downloadFile(`/caisse/folios/${folio.id}/export/`, `folio_${folio.date}.xlsx`)}
              className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-lg"
            >
              <Download className="h-4 w-4" /> Excel
            </button>
          )}
          {!folio.cloture && (
            <>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-3 py-2 rounded-lg"
              >
                <Plus className="h-4 w-4" /> Opération manuelle
              </button>
              {folio.id !== null && (
                <button
                  onClick={() => {
                    if (window.confirm('Clôturer ce folio ? Il deviendra en lecture seule.')) {
                      cloturer.mutate();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium px-3 py-2 rounded-lg"
                >
                  <Lock className="h-4 w-4" /> Clôturer
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Fond de caisse" value={num(folio.fond_initial)} icon={Wallet} color="gray" />
        <KpiCard label="Encaissements" value={num(folio.total_encaissements)} icon={TrendingUp} color="green" />
        <KpiCard label="Décaissements" value={num(folio.total_decaissements)} icon={TrendingDown} color="red" />
        <KpiCard label="Solde théorique" value={num(folio.solde)} icon={Wallet} color="blue" emphasize />
      </div>

      {/* Table opérations */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">
            Mouvements du jour ({operations.length})
          </h3>
        </div>
        {operations.length === 0 ? (
          <div className="p-8 text-center text-gray-400">Aucune opération enregistrée pour cette journée.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-4 py-2">Heure</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Catégorie</th>
                  <th className="px-4 py-2">Libellé</th>
                  <th className="px-4 py-2">Référence</th>
                  <th className="px-4 py-2 text-right">Montant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {operations.map((op) => {
                  const isIn = op.type_operation === 'encaissement';
                  return (
                    <tr key={op.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-500 whitespace-nowrap">
                        {new Date(op.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          isIn ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {isIn ? <ArrowDownCircle className="h-3.5 w-3.5" /> : <ArrowUpCircle className="h-3.5 w-3.5" />}
                          {op.type_operation_display}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">{op.categorie_display}</td>
                      <td className="px-4 py-2 text-sm text-gray-800">{op.libelle}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{op.reference || '—'}</td>
                      <td className={`px-4 py-2 text-sm text-right font-semibold ${isIn ? 'text-green-700' : 'text-red-700'}`}>
                        {isIn ? '+' : '−'} {formatCurrency(num(op.montant))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <OperationManuelleModal
          folioId={folio.id}
          date={date}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'gray' | 'green' | 'red' | 'blue';
  emphasize?: boolean;
}> = ({ label, value, icon: Icon, color, emphasize }) => {
  const colors: Record<string, string> = {
    gray: 'text-gray-600 bg-gray-100',
    green: 'text-green-600 bg-green-100',
    red: 'text-red-600 bg-red-100',
    blue: 'text-blue-600 bg-blue-100',
  };
  return (
    <div className={`bg-white rounded-xl shadow-sm p-4 ${emphasize ? 'ring-2 ring-blue-100' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">{label}</span>
        <span className={`p-1.5 rounded-lg ${colors[color]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <p className={`mt-2 font-bold ${emphasize ? 'text-2xl text-blue-700' : 'text-xl text-gray-900'}`}>
        {formatCurrency(value)}
      </p>
    </div>
  );
};

const OperationManuelleModal: React.FC<{
  folioId: number | null;
  date: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ folioId, date, onClose, onSaved }) => {
  const [type, setType] = useState<TypeOperation>('decaissement');
  const [categorie, setCategorie] = useState<CategorieOperation>('autre');
  const [montant, setMontant] = useState('');
  const [libelle, setLibelle] = useState('');
  const [reference, setReference] = useState('');

  const mutation = useMutation(
    async () => caisseApi.createOperation({
      // Jour passé sans folio : on l'ouvre au moment de la saisie
      folio: folioId ?? ((await caisseApi.getFolioDuJour(date, true)).id as number),
      type_operation: type,
      categorie,
      montant,
      libelle,
      reference,
    }),
    { onSuccess: onSaved },
  );

  const valid = Number(montant) > 0 && libelle.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Nouvelle opération de caisse</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={type} onChange={(e) => setType(e.target.value as TypeOperation)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
                <option value="encaissement">Encaissement</option>
                <option value="decaissement">Décaissement</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select value={categorie} onChange={(e) => setCategorie(e.target.value as CategorieOperation)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
                <option value="autre">Autre</option>
                <option value="consultation">Consultation / Prestation</option>
                <option value="depense">Dépense</option>
                <option value="depot_banque">Dépôt en banque</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant (GNF)</label>
            <input type="number" min={0} value={montant} onChange={(e) => setMontant(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Libellé</label>
            <input type="text" value={libelle} onChange={(e) => setLibelle(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence (optionnel)</label>
            <input type="text" value={reference} onChange={(e) => setReference(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          </div>
          {mutation.isError && (
            <p className="text-sm text-red-600">Erreur lors de l'enregistrement.</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annuler</button>
          <button
            disabled={!valid || mutation.isLoading}
            onClick={() => mutation.mutate()}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50"
          >
            {mutation.isLoading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolioJournalier;
